import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react';

import { DictionaryItemDto, DictionaryTypeDto, UserShortDto } from '@/api/generated';
import { useGetAllDictionaries } from '@/api/requests/useGetAllDictionaries.util.ts';
import { useGetUsersShort } from '@/api/requests/useGetUsersShort.util.ts';
import { SelectSearchOptionModel } from '@/components/form/model/SelectSearchOption.model.ts';
import { FullPageSpinner } from '@/components/ui/fullPageSpinner/FullPageSpinner.comp.tsx';

import {
  DictionariesContext,
  DictionariesContextProps,
  DictionariesDefaultValues,
} from './Dictionaries.context';

const mapDict = (dict: DictionaryItemDto): SelectSearchOptionModel<number> => ({
  label: dict.name,
  value: dict.dictionaryId,
});

const mapUserShort = (user: UserShortDto): SelectSearchOptionModel => ({
  label: `${user.lastName} ${user.firstName}`,
  value: user.userId,
});

export const DictionariesContextProvider: React.FC<PropsWithChildren> = props => {
  const [groupedDictionaries, setGroupedDictionaries] = useState<DictionariesContextProps>({
    ...DictionariesDefaultValues,
  });
  const { isGettingUsersShort, usersShort } = useGetUsersShort();

  const { dictionaries, isFetchingDictionariesTypes } = useGetAllDictionaries();

  useEffect(() => {
    if (dictionaries && !isGettingUsersShort) {
      setGroupedDictionaries({
        [DictionaryTypeDto.CarDeadlineType]:
          dictionaries[DictionaryTypeDto.CarDeadlineType].map(mapDict),
        [DictionaryTypeDto.CarType]: dictionaries[DictionaryTypeDto.CarType].map(mapDict),
        [DictionaryTypeDto.DietaryPreference]:
          dictionaries[DictionaryTypeDto.DietaryPreference].map(mapDict),
        [DictionaryTypeDto.DishDepartment]:
          dictionaries[DictionaryTypeDto.DishDepartment].map(mapDict),
        [DictionaryTypeDto.DishType]: dictionaries[DictionaryTypeDto.DishType].map(mapDict),
        [DictionaryTypeDto.EmergencyContactCategoryType]:
          dictionaries[DictionaryTypeDto.EmergencyContactCategoryType].map(mapDict),
        [DictionaryTypeDto.EventType]: dictionaries[DictionaryTypeDto.EventType].map(mapDict),
        [DictionaryTypeDto.MenuType]: dictionaries[DictionaryTypeDto.MenuType].map(mapDict),
        [DictionaryTypeDto.PackageOfferItem]:
          dictionaries[DictionaryTypeDto.PackageOfferItem].map(mapDict),
        [DictionaryTypeDto.PredefinedOffer]:
          dictionaries[DictionaryTypeDto.PredefinedOffer].map(mapDict),
        [DictionaryTypeDto.SupplierType]: dictionaries[DictionaryTypeDto.SupplierType].map(mapDict),
        [DictionaryTypeDto.Tag]: dictionaries[DictionaryTypeDto.Tag].map(mapDict),
        ['USERS_SHORT']: usersShort.map(mapUserShort),
      });
    }
  }, [dictionaries, isGettingUsersShort, usersShort]);

  const context: DictionariesContextProps = useMemo(
    () => groupedDictionaries,
    [groupedDictionaries],
  );

  if (isFetchingDictionariesTypes) {
    return <FullPageSpinner />;
  }

  return (
    <>
      <DictionariesContext.Provider value={context}>{props.children}</DictionariesContext.Provider>
    </>
  );
};
